import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Modal,
  IconButton,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import InventoryIcon from "@mui/icons-material/Inventory";
import WarningIcon from "@mui/icons-material/Warning";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CategoryIcon from "@mui/icons-material/Category";
import ImageIcon from "@mui/icons-material/Image";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ReplayIcon from "@mui/icons-material/Replay";

// Firebase imports
import { ref, get, push, set, update, remove } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../fb';

// Estilo para os modais
const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 600,
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 4,
  maxHeight: '90vh',
  overflow: 'auto'
};

// Função para formatar moeda
const formatCurrency = (value) => 
  new Intl.NumberFormat('pt-MZ', {
    style: 'currency',
    currency: 'MZN',
    minimumFractionDigits: 2,
  }).format(value).replace('MZN', 'MT');

// Hook useProdutos com Firebase corrigido
const useProdutos = () => {
  const [produtos, setProdutos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [estatisticas, setEstatisticas] = useState(null);

  // Carregar produtos do Firebase
  const carregarProdutos = async (filtros = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('📦 Carregando produtos do Firebase...');
      
      const produtosRef = ref(db, 'produtos');
      const snapshot = await get(produtosRef);
      
      let produtosData = [];
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const produto = childSnapshot.val();
          produtosData.push({
            id: childSnapshot.key,
            ...produto
          });
        });
      }

      // Aplicar filtros localmente
      if (filtros.categoria && filtros.categoria !== 'todos') {
        produtosData = produtosData.filter(p => p.categoria === filtros.categoria);
      }
      
      if (filtros.tipoStock && filtros.tipoStock !== 'todos') {
        produtosData = produtosData.filter(p => p.tipoStock === filtros.tipoStock);
      }
      
      if (filtros.busca) {
        const buscaLower = filtros.busca.toLowerCase();
        produtosData = produtosData.filter(p => 
          p.nome?.toLowerCase().includes(buscaLower) ||
          p.codigo?.toLowerCase().includes(buscaLower) ||
          (p.descricao && p.descricao.toLowerCase().includes(buscaLower))
        );
      }

      // Ordenar por data de criação (mais recente primeiro)
      produtosData.sort((a, b) => new Date(b.dataCriacao || 0) - new Date(a.dataCriacao || 0));

      setProdutos(produtosData);
      console.log(`✅ ${produtosData.length} produtos carregados do Firebase`);
      
    } catch (err) {
      console.error('❌ Erro ao carregar produtos:', err);
      setError('Erro ao carregar produtos do servidor: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Carregar estatísticas do Firebase
  const carregarEstatisticas = async () => {
    try {
      console.log('📊 Carregando estatísticas do Firebase...');
      
      const produtosRef = ref(db, 'produtos');
      const snapshot = await get(produtosRef);
      
      let produtosData = [];
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const produto = childSnapshot.val();
          produtosData.push({
            id: childSnapshot.key,
            ...produto
          });
        });
      }

      // Filtrar apenas produtos ativos para estatísticas
      const produtosAtivos = produtosData.filter(p => p.ativo !== false);

      const totalProdutos = produtosAtivos.length;
      const produtosStockBaixo = produtosAtivos.filter(p => 
        p.tipoStock === 'fisico' && 
        (p.quantidade || 0) > 0 && 
        (p.quantidade || 0) <= (p.stockMinimo || 0)
      ).length;
      
      const produtosSemStock = produtosAtivos.filter(p => 
        p.tipoStock === 'fisico' && (p.quantidade || 0) === 0
      ).length;
      
      const produtosPorEncomenda = produtosAtivos.filter(p => 
        p.tipoStock === 'encomenda'
      ).length;

      const lucroPotencialTotal = produtosAtivos
        .filter(p => p.tipoStock === 'fisico')
        .reduce((acc, p) => {
          const precoVenda = p.precoVenda || 0;
          const precoCompra = p.precoCompra || 0;
          const quantidade = p.quantidade || 0;
          return acc + ((precoVenda - precoCompra) * quantidade);
        }, 0);

      const stats = {
        totalProdutos,
        produtosStockBaixo,
        produtosSemStock,
        produtosPorEncomenda,
        lucroPotencialTotal,
        totalProdutosInativos: produtosData.filter(p => p.ativo === false).length
      };

      setEstatisticas(stats);
      console.log('✅ Estatísticas carregadas:', stats);
      
    } catch (err) {
      console.error('❌ Erro ao carregar estatísticas:', err);
      setEstatisticas({
        totalProdutos: 0,
        produtosStockBaixo: 0,
        produtosSemStock: 0,
        produtosPorEncomenda: 0,
        lucroPotencialTotal: 0,
        totalProdutosInativos: 0
      });
    }
  };

  // Upload de imagem para Firebase Storage
  const uploadImagem = async (file, onProgress = null) => {
    try {
      console.log('📤 Iniciando upload para Firebase Storage...', file.name);
      
      // Criar referência única para o arquivo
      const fileExtension = file.name.split('.').pop();
      const fileName = `produtos/${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExtension}`;
      const imageRef = storageRef(storage, fileName);

      // Fazer upload do arquivo
      const snapshot = await uploadBytes(imageRef, file);
      
      // Obter URL de download
      const downloadURL = await getDownloadURL(snapshot.ref);

      console.log('✅ Upload concluído:', downloadURL);

      return {
        success: true,
        data: {
          filename: fileName,
          originalname: file.name,
          size: file.size,
          url: downloadURL,
          path: fileName
        }
      };
      
    } catch (err) {
      console.error('❌ Erro no upload:', err);
      throw new Error('Erro ao fazer upload da imagem: ' + err.message);
    }
  };

  // Deletar imagem do Firebase Storage
  const deletarImagem = async (imagePath) => {
    try {
      if (!imagePath) return;
      
      console.log('🗑️ Deletando imagem:', imagePath);
      const imageRef = storageRef(storage, imagePath);
      await deleteObject(imageRef);
      console.log('✅ Imagem deletada com sucesso');
      
    } catch (err) {
      console.error('❌ Erro ao deletar imagem:', err);
      // Não lançar erro para não interromper o fluxo principal
    }
  };

  // Criar produto no Firebase
  const criarProduto = async (produtoData) => {
    setLoading(true);
    try {
      console.log('➕ Criando produto no Firebase...', produtoData);
      
      // Verificar se código já existe (apenas em produtos ativos)
      if (produtoData.codigo) {
        const produtosRef = ref(db, 'produtos');
        const snapshot = await get(produtosRef);
        
        let codigoExiste = false;
        if (snapshot.exists()) {
          snapshot.forEach((childSnapshot) => {
            const produto = childSnapshot.val();
            if (produto.codigo === produtoData.codigo && produto.ativo !== false) {
              codigoExiste = true;
            }
          });
        }

        if (codigoExiste) {
          throw new Error('Código do produto já existe');
        }
      }

      const novoProduto = {
        ...produtoData,
        ativo: true,
        dataCriacao: new Date().toISOString(),
        dataAtualizacao: new Date().toISOString()
      };

      // Salvar no Firebase
      const produtoRef = push(ref(db, 'produtos'));
      await set(produtoRef, novoProduto);

      const produtoSalvo = {
        id: produtoRef.key,
        ...novoProduto
      };

      // Atualizar lista local
      setProdutos(prev => [produtoSalvo, ...prev]);
      
      // Recarregar estatísticas
      await carregarEstatisticas();
      
      console.log('✅ Produto criado:', produtoSalvo);
      return produtoSalvo;
      
    } catch (err) {
      console.error('❌ Erro ao criar produto:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Atualizar produto no Firebase
  const atualizarProduto = async (id, produtoData) => {
    setLoading(true);
    try {
      console.log('✏️ Atualizando produto no Firebase...', id);
      
      // Verificar se produto existe
      const produtoRef = ref(db, `produtos/${id}`);
      const snapshot = await get(produtoRef);
      
      if (!snapshot.exists()) {
        throw new Error('Produto não encontrado');
      }

      const produtoAntigo = snapshot.val();

      // Se a imagem foi removida, deletar do storage
      if (produtoAntigo.imagemPath && !produtoData.imagem) {
        await deletarImagem(produtoAntigo.imagemPath);
      }

      const updates = {
        ...produtoData,
        dataAtualizacao: new Date().toISOString()
      };

      await update(produtoRef, updates);

      const produtoAtualizado = {
        id,
        ...produtoAntigo,
        ...updates
      };

      // Atualizar lista local
      setProdutos(prev => prev.map(p => p.id === id ? produtoAtualizado : p));
      
      // Recarregar estatísticas
      await carregarEstatisticas();
      
      console.log('✅ Produto atualizado:', produtoAtualizado);
      return produtoAtualizado;
      
    } catch (err) {
      console.error('❌ Erro ao atualizar produto:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Excluir produto (soft delete) no Firebase
  const excluirProduto = async (id) => {
    setLoading(true);
    try {
      console.log('🗑️ Excluindo produto no Firebase...', id);
      
      const produtoRef = ref(db, `produtos/${id}`);
      const snapshot = await get(produtoRef);
      
      if (!snapshot.exists()) {
        throw new Error('Produto não encontrado');
      }

      const produto = snapshot.val();

      // Soft delete - marcar como inativo
      await update(produtoRef, {
        ativo: false,
        dataAtualizacao: new Date().toISOString()
      });

      // Atualizar lista local (não remover, apenas marcar como inativo)
      setProdutos(prev => prev.map(p => 
        p.id === id ? { ...p, ativo: false } : p
      ));
      
      // Recarregar estatísticas
      await carregarEstatisticas();
      
      console.log('✅ Produto excluído:', id);
      
    } catch (err) {
      console.error('❌ Erro ao excluir produto:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Restaurar produto (ativar novamente)
  const restaurarProduto = async (id) => {
    setLoading(true);
    try {
      console.log('🔄 Restaurando produto...', id);
      
      const produtoRef = ref(db, `produtos/${id}`);
      const snapshot = await get(produtoRef);
      
      if (!snapshot.exists()) {
        throw new Error('Produto não encontrado');
      }

      await update(produtoRef, {
        ativo: true,
        dataAtualizacao: new Date().toISOString()
      });

      // Atualizar lista local
      setProdutos(prev => prev.map(p => 
        p.id === id ? { ...p, ativo: true } : p
      ));
      
      // Recarregar estatísticas
      await carregarEstatisticas();
      
      console.log('✅ Produto restaurado:', id);
      
    } catch (err) {
      console.error('❌ Erro ao restaurar produto:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Limpar erro
  const limparErro = () => setError(null);

  // Carregar dados iniciais - CORRIGIDO PARA EVITAR LOOP
  useEffect(() => {
    console.log('🎯 Iniciando carregamento de dados do Firebase...');
    carregarProdutos();
    carregarEstatisticas();
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    produtos: Array.isArray(produtos) ? produtos : [],
    loading,
    error,
    estatisticas,
    carregarProdutos,
    carregarEstatisticas,
    criarProduto,
    atualizarProduto,
    excluirProduto,
    restaurarProduto,
    uploadImagem,
    deletarImagem,
    limparErro
  };
};

// Componente de Visualização de Produto
const ProdutoViewer = ({ produto, formatCurrency, onEdit }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <IconButton 
        size="small"
        onClick={() => setOpen(true)}
        title="Ver Detalhes"
        color="info"
      >
        <VisibilityIcon />
      </IconButton>
      
      <Modal open={open} onClose={() => setOpen(false)}>
        <Box sx={{...modalStyle, width: 500}}>
          <Typography variant="h5" sx={{ mb: 3 }}>
            {produto.nome}
            {produto.ativo === false && (
              <Chip 
                label="INATIVO" 
                size="small" 
                color="error"
                sx={{ ml: 2 }}
              />
            )}
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              {produto.imagem ? (
                <Box
                  component="img"
                  src={produto.imagem}
                  alt={produto.nome}
                  sx={{ 
                    width: '100%', 
                    height: 200, 
                    objectFit: 'cover', 
                    borderRadius: 1 
                  }}
                />
              ) : (
                <Box
                  sx={{
                    height: 200,
                    backgroundColor: '#f5f5f5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 1,
                  }}
                >
                  <ImageIcon sx={{ fontSize: 60, color: '#ccc' }} />
                </Box>
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  Preço de Compra
                </Typography>
                <Typography variant="h6" color="primary.main">
                  {formatCurrency(produto.precoCompra || 0)}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  Preço de Venda
                </Typography>
                <Typography variant="h6" color="success.main">
                  {formatCurrency(produto.precoVenda || 0)}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  Margem de Lucro
                </Typography>
                <Typography variant="h6" color="info.main">
                  {formatCurrency((produto.precoVenda || 0) - (produto.precoCompra || 0))}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  Stock Disponível
                </Typography>
                <Typography 
                  variant="h6" 
                  color={
                    produto.tipoStock === 'encomenda' ? 'warning.main' :
                    (produto.quantidade || 0) === 0 ? 'error.main' : 
                    'success.main'
                  }
                >
                  {produto.tipoStock === 'encomenda' ? 'Por Encomenda' : `${produto.quantidade || 0} unidades`}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  Categoria
                </Typography>
                <Chip 
                  label={produto.categoria} 
                  size="small" 
                  color="primary"
                  variant="outlined"
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  Estado
                </Typography>
                <Chip 
                  label={produto.ativo === false ? 'Inativo' : 'Ativo'} 
                  size="small" 
                  color={produto.ativo === false ? 'error' : 'success'}
                />
              </Box>
            </Grid>
          </Grid>

          {produto.descricao && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="textSecondary">
                Descrição/Especificações
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                {produto.descricao}
              </Typography>
            </Box>
          )}

          {produto.especificacoes && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="textSecondary">
                Especificações Técnicas
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                {produto.especificacoes}
              </Typography>
            </Box>
          )}

          {(produto.quantidade || 0) <= (produto.stockMinimo || 0) && produto.tipoStock !== 'encomenda' && produto.ativo !== false && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Stock baixo! Reabastecer urgentemente.
            </Alert>
          )}

          <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
            {produto.ativo !== false && (
              <Button 
                variant="outlined" 
                onClick={() => {
                  setOpen(false);
                  onEdit(produto);
                }}
                startIcon={<EditIcon />}
              >
                Editar
              </Button>
            )}
            <Button 
              variant="contained" 
              onClick={() => setOpen(false)}
              sx={{ ml: 'auto' }}
            >
              Fechar
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

// Componente de Upload de Imagem ATUALIZADO para Firebase Storage
const ImageUploader = ({ onImageUpload, currentImage, loading, produtoId }) => {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Verificar se é uma imagem
    if (!file.type.startsWith('image/')) {
      alert('Por favor, selecione apenas arquivos de imagem.');
      return;
    }

    // Verificar tamanho do arquivo (5MB máximo)
    if (file.size > 5 * 1024 * 1024) {
      alert('Arquivo muito grande. O tamanho máximo é 5MB.');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Criar preview local imediato
      const reader = new FileReader();
      reader.onload = (e) => {
        // Enviar arquivo para Firebase Storage
        onImageUpload(file, e.target.result, setUploadProgress);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Erro ao processar imagem:', error);
      alert('Erro ao processar a imagem. Tente novamente.');
      setIsUploading(false);
    }
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 1 }}>
        Imagem do Produto
      </Typography>
      
      {currentImage ? (
        <Box sx={{ position: 'relative', mb: 2 }}>
          <Box
            component="img"
            src={currentImage}
            alt="Preview"
            sx={{ 
              width: '100%', 
              height: 200, 
              objectFit: 'cover', 
              borderRadius: 1 
            }}
          />
          <Button
            size="small"
            color="error"
            onClick={() => onImageUpload(null)}
            sx={{ position: 'absolute', top: 8, right: 8 }}
            disabled={loading || isUploading}
          >
            Remover
          </Button>
        </Box>
      ) : (
        <Box
          sx={{
            border: '2px dashed #ccc',
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
            mb: 2,
            cursor: loading || isUploading ? 'default' : 'pointer',
            backgroundColor: loading || isUploading ? '#f5f5f5' : 'transparent',
            opacity: loading || isUploading ? 0.6 : 1
          }}
          onClick={() => !loading && !isUploading && document.getElementById('image-upload').click()}
        >
          {isUploading ? (
            <Box>
              <CloudUploadIcon sx={{ fontSize: 48, color: '#1976d2', mb: 1 }} />
              <Typography variant="body2" color="primary">
                Fazendo upload... {uploadProgress}%
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={uploadProgress} 
                sx={{ mt: 1 }}
              />
            </Box>
          ) : (
            <Box>
              <ImageIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
              <Typography variant="body2" color="textSecondary">
                Clique para adicionar imagem
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Suporta até 5MB - JPG, PNG, WebP
              </Typography>
            </Box>
          )}
        </Box>
      )}

      <input
        id="image-upload"
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
        disabled={loading || isUploading}
      />
      
      {!currentImage && !isUploading && (
        <Button
          fullWidth
          variant="outlined"
          startIcon={<CloudUploadIcon />}
          onClick={() => document.getElementById('image-upload').click()}
          disabled={loading}
        >
          Selecionar Imagem
        </Button>
      )}

      {isUploading && (
        <Alert severity="info" sx={{ mt: 1 }}>
          Fazendo upload para o servidor... {uploadProgress}% completo
        </Alert>
      )}
    </Box>
  );
};

export default function Produtos() {
  const {
    produtos,
    loading,
    error,
    estatisticas,
    carregarProdutos,
    carregarEstatisticas,
    criarProduto,
    atualizarProduto,
    excluirProduto,
    restaurarProduto,
    uploadImagem,
    deletarImagem,
    limparErro
  } = useProdutos();

  // Estados para modais
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [uploading, setUploading] = useState(false);
  
  // Estado para o produto sendo editado/adicionado
  const [produtoData, setProdutoData] = useState({
    id: null,
    codigo: "",
    nome: "",
    descricao: "",
    especificacoes: "",
    precoCompra: "",
    precoVenda: "",
    quantidade: "",
    stockMinimo: "",
    categoria: "",
    tipoStock: "fisico",
    imagem: "",
    imagemPath: ""
  });

  // Estados para filtros
  const [filtroCategoria, setFiltroCategoria] = useState('todos');
  const [busca, setBusca] = useState('');
  const [filtroStock, setFiltroStock] = useState('todos');
  const [filtroAtivo, setFiltroAtivo] = useState('ativos');

  // Garantir que produtos é sempre um array
  const produtosArray = Array.isArray(produtos) ? produtos : [];
  
  // Categorias disponíveis
  const categorias = [...new Set(produtosArray.map(p => p.categoria).filter(Boolean))];

  // Mostrar snackbar
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Função para carregar produtos manualmente - CORRIGIDA
  const handleCarregarProdutos = useCallback(async () => {
    try {
      await carregarProdutos();
    } catch (err) {
      console.error('Erro ao carregar produtos:', err);
      showSnackbar('Erro ao carregar produtos', 'error');
    }
  }, [carregarProdutos]);

  // Função para lidar com upload de imagem ATUALIZADA para Firebase Storage
  const handleImageUpload = async (file, previewUrl, progressCallback = null) => {
    if (!file) {
      // Remover imagem
      setProdutoData(prev => ({ 
        ...prev, 
        imagem: "", 
        imagemPath: "" 
      }));
      return;
    }

    setUploading(true);

    try {
      // Fazer upload para Firebase Storage
      const uploadResult = await uploadImagem(file, progressCallback);
      
      if (uploadResult.success) {
        setProdutoData(prev => ({ 
          ...prev, 
          imagem: uploadResult.data.url,
          imagemPath: uploadResult.data.path
        }));
        showSnackbar('Imagem carregada com sucesso!', 'success');
      }
    } catch (error) {
      console.error('Erro ao fazer upload da imagem:', error);
      setProdutoData(prev => ({ ...prev, imagem: "", imagemPath: "" }));
      showSnackbar('Erro ao carregar imagem: ' + error.message, 'error');
    } finally {
      setUploading(false);
    }
  };

  /* --- FUNÇÕES DE FILTRO (LOCAL) --- */
  const produtosFiltrados = produtosArray.filter(produto => {
    const matchBusca = !busca || 
      produto.nome?.toLowerCase().includes(busca.toLowerCase()) ||
      produto.codigo?.toLowerCase().includes(busca.toLowerCase());
    
    const matchCategoria = !filtroCategoria || 
      filtroCategoria === 'todos' || 
      produto.categoria === filtroCategoria;
    
    const matchAtivo = 
      filtroAtivo === 'todos' ? true :
      filtroAtivo === 'ativos' ? produto.ativo !== false :
      produto.ativo === false;

    let matchStock = true;
    if (filtroStock && filtroStock !== 'todos') {
      switch(filtroStock) {
        case 'baixo':
          matchStock = produto.tipoStock === 'fisico' && 
                      (produto.quantidade || 0) <= (produto.stockMinimo || 0);
          break;
        case 'encomenda':
          matchStock = produto.tipoStock === 'encomenda';
          break;
        case 'semStock':
          matchStock = produto.tipoStock === 'fisico' && (produto.quantidade || 0) === 0;
          break;
        case 'normal':
          matchStock = produto.tipoStock === 'fisico' && 
                      (produto.quantidade || 0) > (produto.stockMinimo || 0);
          break;
        default:
          matchStock = true;
      }
    }

    return matchBusca && matchCategoria && matchAtivo && matchStock;
  });

  /* --- FUNÇÕES DE PRODUTOS --- */
  const handleOpenAdd = () => {
    const novoCodigo = `PROD${String(produtosArray.length + 1).padStart(3, '0')}`;
    
    setProdutoData({
      id: null,
      codigo: novoCodigo,
      nome: "",
      descricao: "",
      especificacoes: "",
      precoCompra: "",
      precoVenda: "",
      quantidade: "",
      stockMinimo: "",
      categoria: "",
      tipoStock: "fisico",
      imagem: "",
      imagemPath: ""
    });
    setOpenAddModal(true);
  };

  const handleOpenEdit = (produto) => {
    setProdutoData({ 
      ...produto,
      precoCompra: produto.precoCompra?.toString() || "",
      precoVenda: produto.precoVenda?.toString() || "",
      quantidade: produto.quantidade?.toString() || "",
      stockMinimo: produto.stockMinimo?.toString() || ""
    });
    setOpenEditModal(true);
  };

  const handleSaveProduto = async () => {
    if (!produtoData.nome || !produtoData.precoCompra || !produtoData.precoVenda || !produtoData.categoria) {
      showSnackbar('Preencha os campos obrigatórios', 'error');
      return;
    }

    try {
      const produtoParaSalvar = {
        codigo: produtoData.codigo,
        nome: produtoData.nome,
        descricao: produtoData.descricao,
        especificacoes: produtoData.especificacoes,
        precoCompra: parseFloat(produtoData.precoCompra),
        precoVenda: parseFloat(produtoData.precoVenda),
        quantidade: parseInt(produtoData.quantidade) || 0,
        stockMinimo: parseInt(produtoData.stockMinimo) || 0,
        categoria: produtoData.categoria,
        tipoStock: produtoData.tipoStock,
        imagem: produtoData.imagem,
        imagemPath: produtoData.imagemPath
      };

      if (produtoData.id) {
        // Editar produto existente
        await atualizarProduto(produtoData.id, produtoParaSalvar);
        showSnackbar('Produto atualizado com sucesso!');
        setOpenEditModal(false);
      } else {
        // Adicionar novo produto
        await criarProduto(produtoParaSalvar);
        showSnackbar('Produto criado com sucesso!');
        setOpenAddModal(false);
      }

      // Limpar dados
      setProdutoData({
        id: null,
        codigo: "",
        nome: "",
        descricao: "",
        especificacoes: "",
        precoCompra: "",
        precoVenda: "",
        quantidade: "",
        stockMinimo: "",
        categoria: "",
        tipoStock: "fisico",
        imagem: "",
        imagemPath: ""
      });

    } catch (err) {
      showSnackbar(err.message || 'Erro ao salvar produto', 'error');
    }
  };

  const handleDeleteProduto = async (id) => {
    if (window.confirm("Tem certeza que deseja eliminar este produto?")) {
      try {
        await excluirProduto(id);
        showSnackbar('Produto excluído com sucesso!');
      } catch (err) {
        showSnackbar('Erro ao excluir produto', 'error');
      }
    }
  };

  const handleRestaurarProduto = async (id) => {
    if (window.confirm("Tem certeza que deseja restaurar este produto?")) {
      try {
        await restaurarProduto(id);
        showSnackbar('Produto restaurado com sucesso!');
      } catch (err) {
        showSnackbar('Erro ao restaurar produto', 'error');
      }
    }
  };

  // Carregar produtos quando o componente montar - CORRIGIDO
  useEffect(() => {
    handleCarregarProdutos();
  }, []); // Removida a dependência que causava loop

  // Limpar erro quando o componente desmontar
  useEffect(() => {
    return () => {
      limparErro();
    };
  }, [limparErro]);

  // Extrair dados das estatísticas
  const estatisticasData = estatisticas || {
    totalProdutos: 0,
    produtosStockBaixo: 0,
    produtosSemStock: 0,
    produtosPorEncomenda: 0,
    lucroPotencialTotal: 0,
    totalProdutosInativos: 0
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* CABEÇALHO */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          📦 Gestão de Produtos
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button 
            variant="outlined" 
            onClick={handleCarregarProdutos}
            disabled={loading}
            startIcon={<InventoryIcon />}
          >
            Atualizar
          </Button>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={handleOpenAdd}
            disabled={loading}
          >
            Novo Produto
          </Button>
        </Box>
      </Box>

      {/* ESTATÍSTICAS */}
      {estatisticasData && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <InventoryIcon color="primary" sx={{ mb: 1 }} />
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Total Produtos
                </Typography>
                <Typography variant="h5" component="div">
                  {estatisticasData.totalProdutos || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <WarningIcon color="warning" sx={{ mb: 1 }} />
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Stock Baixo
                </Typography>
                <Typography variant="h5" component="div" color="warning.main">
                  {estatisticasData.produtosStockBaixo || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <WarningIcon color="error" sx={{ mb: 1 }} />
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Sem Stock
                </Typography>
                <Typography variant="h5" component="div" color="error.main">
                  {estatisticasData.produtosSemStock || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <CategoryIcon color="info" sx={{ mb: 1 }} />
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Por Encomenda
                </Typography>
                <Typography variant="h5" component="div" color="info.main">
                  {estatisticasData.produtosPorEncomenda || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <AttachMoneyIcon color="success" sx={{ mb: 1 }} />
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Lucro Potencial
                </Typography>
                <Typography variant="h5" component="div" color="success.main">
                  {formatCurrency(estatisticasData.lucroPotencialTotal || 0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <DeleteIcon color="action" sx={{ mb: 1 }} />
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Produtos Inativos
                </Typography>
                <Typography variant="h5" component="div" color="text.secondary">
                  {estatisticasData.totalProdutosInativos || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* FILTROS E BUSCA */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Buscar produto ou código..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Categoria</InputLabel>
              <Select
                value={filtroCategoria}
                label="Categoria"
                onChange={(e) => setFiltroCategoria(e.target.value)}
              >
                <MenuItem value="todos">Todas Categorias</MenuItem>
                {categorias.map(cat => (
                  <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Estado do Stock</InputLabel>
              <Select
                value={filtroStock}
                label="Estado do Stock"
                onChange={(e) => setFiltroStock(e.target.value)}
              >
                <MenuItem value="todos">Todos</MenuItem>
                <MenuItem value="normal">Stock Normal</MenuItem>
                <MenuItem value="baixo">Stock Baixo</MenuItem>
                <MenuItem value="semStock">Sem Stock</MenuItem>
                <MenuItem value="encomenda">Por Encomenda</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Estado</InputLabel>
              <Select
                value={filtroAtivo}
                label="Estado"
                onChange={(e) => setFiltroAtivo(e.target.value)}
              >
                <MenuItem value="ativos">Ativos</MenuItem>
                <MenuItem value="inativos">Inativos</MenuItem>
                <MenuItem value="todos">Todos</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* LISTA DE PRODUTOS */}
      <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 6 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Lista de Produtos ({produtosFiltrados.length})
          </Typography>
          {loading && <CircularProgress size={24} />}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={limparErro}>
            {error}
          </Alert>
        )}

        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell><strong>Produto</strong></TableCell>
              <TableCell><strong>Código</strong></TableCell>
              <TableCell><strong>Categoria</strong></TableCell>
              <TableCell><strong>Compra</strong></TableCell>
              <TableCell><strong>Venda</strong></TableCell>
              <TableCell><strong>Lucro</strong></TableCell>
              <TableCell><strong>Stock</strong></TableCell>
              <TableCell><strong>Estado Stock</strong></TableCell>
              <TableCell><strong>Estado</strong></TableCell>
              <TableCell align="center"><strong>Ações</strong></TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {produtosFiltrados.map((produto) => {
              const lucroUnitario = (produto.precoVenda || 0) - (produto.precoCompra || 0);
              const estadoStock = produto.tipoStock === 'encomenda' ? 'encomenda' :
                                (produto.quantidade || 0) === 0 ? 'semStock' : 
                                (produto.quantidade || 0) <= (produto.stockMinimo || 0) ? 'baixo' : 'normal';

              return (
                <TableRow 
                  key={produto.id} 
                  hover
                  sx={{ 
                    opacity: produto.ativo === false ? 0.7 : 1,
                    backgroundColor: produto.ativo === false ? '#fafafa' : 'inherit'
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {produto.imagem ? (
                        <Box
                          component="img"
                          src={produto.imagem}
                          alt={produto.nome}
                          sx={{ 
                            width: 40, 
                            height: 40, 
                            objectFit: 'cover', 
                            borderRadius: 1 
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            backgroundColor: '#f5f5f5',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: 1,
                          }}
                        >
                          <ImageIcon sx={{ color: '#ccc' }} />
                        </Box>
                      )}
                      <Box>
                        <Typography fontWeight="medium">{produto.nome}</Typography>
                        <Typography variant="body2" color="textSecondary">
                          {produto.descricao}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{produto.codigo}</TableCell>
                  <TableCell>
                    <Chip 
                      label={produto.categoria} 
                      size="small" 
                      variant="outlined"
                      icon={<CategoryIcon />}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    {formatCurrency(produto.precoCompra || 0)}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: 'success.main' }}>
                    {formatCurrency(produto.precoVenda || 0)}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: 'info.main' }}>
                    {formatCurrency(lucroUnitario)}
                  </TableCell>
                  <TableCell>
                    <Typography 
                      fontWeight="bold"
                      color={
                        estadoStock === 'semStock' ? 'error.main' :
                        estadoStock === 'baixo' ? 'warning.main' :
                        estadoStock === 'encomenda' ? 'info.main' : 'success.main'
                      }
                    >
                      {produto.tipoStock === 'encomenda' ? 'Encomenda' : `${produto.quantidade || 0} uni`}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={
                        estadoStock === 'semStock' ? 'Sem Stock' :
                        estadoStock === 'baixo' ? 'Stock Baixo' :
                        estadoStock === 'encomenda' ? 'Por Encomenda' : 'Normal'
                      }
                      size="small"
                      color={
                        estadoStock === 'semStock' ? 'error' :
                        estadoStock === 'baixo' ? 'warning' :
                        estadoStock === 'encomenda' ? 'info' : 'success'
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={produto.ativo === false ? 'Inativo' : 'Ativo'} 
                      size="small" 
                      color={produto.ativo === false ? 'error' : 'success'}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <ProdutoViewer 
                      produto={produto} 
                      formatCurrency={formatCurrency}
                      onEdit={handleOpenEdit}
                    />
                    
                    {produto.ativo !== false ? (
                      <>
                        <IconButton 
                          color="primary" 
                          size="small"
                          onClick={() => handleOpenEdit(produto)}
                          title="Editar Produto"
                          disabled={loading}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton 
                          color="error" 
                          size="small"
                          onClick={() => handleDeleteProduto(produto.id)}
                          title="Eliminar Produto"
                          disabled={loading}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </>
                    ) : (
                      <IconButton 
                        color="success" 
                        size="small"
                        onClick={() => handleRestaurarProduto(produto.id)}
                        title="Restaurar Produto"
                        disabled={loading}
                      >
                        <ReplayIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        {produtosFiltrados.length === 0 && !loading && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {produtosArray.length === 0 
              ? 'Nenhum produto cadastrado. Clique em "Novo Produto" para começar.' 
              : 'Nenhum produto encontrado com os filtros aplicados.'
            }
          </Alert>
        )}
      </Paper>

      {/* MODAL DE ADICIONAR/EDITAR PRODUTO */}
      <Dialog 
        open={openAddModal || openEditModal} 
        onClose={() => {
          setOpenAddModal(false);
          setOpenEditModal(false);
        }} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          {produtoData.id ? 'Editar Produto' : 'Adicionar Novo Produto'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Coluna da Imagem */}
            <Grid item xs={12} sm={6}>
              <ImageUploader 
                onImageUpload={handleImageUpload}
                currentImage={produtoData.imagem}
                loading={loading || uploading}
                produtoId={produtoData.id}
              />
            </Grid>

            {/* Coluna dos Dados */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Código do Produto"
                value={produtoData.codigo}
                onChange={(e) => setProdutoData({ ...produtoData, codigo: e.target.value })}
                sx={{ mb: 2 }}
                disabled={!!produtoData.id}
              />
              
              <TextField
                fullWidth
                label="Nome do Produto *"
                value={produtoData.nome}
                onChange={(e) => setProdutoData({ ...produtoData, nome: e.target.value })}
                sx={{ mb: 2 }}
                required
                error={!produtoData.nome}
              />

              <TextField
                fullWidth
                label="Descrição Curta"
                value={produtoData.descricao}
                onChange={(e) => setProdutoData({ ...produtoData, descricao: e.target.value })}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Especificações Técnicas"
                value={produtoData.especificacoes}
                onChange={(e) => setProdutoData({ ...produtoData, especificacoes: e.target.value })}
                multiline
                rows={3}
                sx={{ mb: 2 }}
                placeholder="Ex: Tamanho: 14'&#10;Capacidade: 256GB&#10;Cor: Preto"
              />

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Preço de Compra (MT) *"
                    type="number"
                    value={produtoData.precoCompra}
                    onChange={(e) => setProdutoData({ ...produtoData, precoCompra: e.target.value })}
                    sx={{ mb: 2 }}
                    required
                    error={!produtoData.precoCompra}
                    inputProps={{ min: "0.01", step: "0.01" }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Preço de Venda (MT) *"
                    type="number"
                    value={produtoData.precoVenda}
                    onChange={(e) => setProdutoData({ ...produtoData, precoVenda: e.target.value })}
                    sx={{ mb: 2 }}
                    required
                    error={!produtoData.precoVenda}
                    inputProps={{ min: "0.01", step: "0.01" }}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Tipo de Stock</InputLabel>
                    <Select
                      value={produtoData.tipoStock}
                      label="Tipo de Stock"
                      onChange={(e) => setProdutoData({ ...produtoData, tipoStock: e.target.value })}
                    >
                      <MenuItem value="fisico">Stock Físico</MenuItem>
                      <MenuItem value="encomenda">Por Encomenda</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Categoria *</InputLabel>
                    <Select
                      value={produtoData.categoria}
                      label="Categoria"
                      onChange={(e) => setProdutoData({ ...produtoData, categoria: e.target.value })}
                      error={!produtoData.categoria}
                    >
                      <MenuItem value=""><em>Selecionar...</em></MenuItem>
                      <MenuItem value="Beleza">Beleza</MenuItem>
                      <MenuItem value="Electrónica">Electrónica</MenuItem>
                      <MenuItem value="Informática">Informática</MenuItem>
                      <MenuItem value="Telefonia">Telefonia</MenuItem>
                      <MenuItem value="Acessórios">Acessórios</MenuItem>
                      <MenuItem value="Outros">Outros</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {produtoData.tipoStock === 'fisico' && (
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Quantidade em Stock"
                      type="number"
                      value={produtoData.quantidade}
                      onChange={(e) => setProdutoData({ ...produtoData, quantidade: e.target.value })}
                      sx={{ mb: 2 }}
                      inputProps={{ min: "0" }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Stock Mínimo"
                      type="number"
                      value={produtoData.stockMinimo}
                      onChange={(e) => setProdutoData({ ...produtoData, stockMinimo: e.target.value })}
                      sx={{ mb: 2 }}
                      inputProps={{ min: "0" }}
                    />
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setOpenAddModal(false);
              setOpenEditModal(false);
            }}
            disabled={loading || uploading}
          >
            Cancelar
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSaveProduto}
            disabled={
              !produtoData.nome || 
              !produtoData.precoCompra || 
              !produtoData.precoVenda || 
              !produtoData.categoria || 
              loading || 
              uploading
            }>
            {loading || uploading ? (
              <CircularProgress size={24} />
            ) : (
              produtoData.id ? 'Atualizar' : 'Adicionar'
            )} Produto
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}